import type { FC } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Container,
  HeaderBreadcrumbs,
  LoadingContainer,
  useErrorSnackbar,
  useIdleTimeoutCallback,
} from '@pflegenavi/web-components';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import type { FormikHelpers } from 'formik';
import { Alert, CircularProgress, Stack, Tooltip } from '@mui/material';
import * as Yup from 'yup';
import type { ReceiptBatchFormValues } from '../components/receipt-batch/interfaces/ReceiptBatchFormValues';
import {
  isSubmitReceiptBatchNursingHomeInfoIncompleteException,
  isSubmitReceiptBatchResidentInfoIncompleteException,
  isSubmitReceiptBatchServiceProviderInfoIncompleteException,
  isSubmitReceiptBatchServiceProviderNotOnboardedException,
  isSubmitReceiptBatchServiceProviderUserIncompleteException,
  useGetServiceProviderPhoenixNoSuspense,
  useDeleteReceiptBatchPhoenix,
  useGenerateReceiptBatchEntryPreview,
  useGenerateReceiptBatchPdf,
  useGenerateReceiptPdf,
  usePatchReceiptBatchPhoenix,
  useRetractReceiptBatchPhoenix,
  useSubmitReceiptBatchPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { getGlobalServiceProviderIdFromToken } from '../utils/getGlobalServiceProviderIdFromToken';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { ReceiptBatchDetailsContainer } from '../components/receipt-batch/ReceiptBatchDetailsContainer';
import {
  isServiceProviderReceiptBatchReadyToSubmit,
  ReceiptBatchState,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { useServiceProviderRenderReceiptBatchRow } from '../components/receipt-batch/useServiceProviderRenderReceiptBatchRow';
import { renderReceiptBatchImageInput } from '../components/receipt-batch/ReceiptBatchImageInput';
import { usePreviewImageIds } from '../components/receipt-batch/utils/usePreviewImageIds';
import { renderReceiptBatchCommonFormEdit } from '../components/receipt-batch/ReceiptBatchCommonForm';
import { useAugmentedThrottledReceipts } from '../components/receipt-batch/utils/useAugmentedThrottledReceipts';
import { EditOrRetractReceiptBatchButton } from '../components/receipt-batch/EditOrRetractReceiptBatchButton';
import { RetractReceiptBatchModal } from '../components/modals/RetractReceiptBatchModal';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import { ServiceProviderReceiptBatchForm } from '../components/receipt-batch/form/ServiceProviderReceiptBatchForm';
import { ReceiptBatchProvider } from '../components/receipt-batch/ReceiptBatchContext';
import { ServiceProviderSubmitReceiptBatchModal } from '../components/receipt-batch/ServiceProviderSubmitReceiptBatchModal';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import {
  transformReceiptBatchFormValuesToUpdateReceiptBatchPhoenix,
  transformReceiptBatchPhoenixToReceiptBatchValues,
} from '../functions/transformReceiptBatchFormValuesToUpdateReceiptBatchPhoenix';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { useHandleFailedPreviewGenerationError } from '../hooks/useHandleFailedPreviewGenerationError';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface EditReceiptBatchPageProps {}

export const EditReceiptBatchPage: FC<EditReceiptBatchPageProps> = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  const { batchId } = useParams();
  const navigate = useNavigate();

  const [dirty, setDirty] = React.useState(false);
  const unblockRef = useRef<() => void>(() => {
    return;
  });

  const { mutateAsync: submitReceiptBatch, isPending: isSubmitting } =
    useSubmitReceiptBatchPhoenix();

  const { data: serviceProvider } = useGetServiceProviderPhoenixNoSuspense();

  // Mirrors the backend onboarding gate: MangoPay providers may only submit
  // once KYC is verified and their payout recipient is active. Stripe
  // (deprecated) providers are not blocked here. The backend remains the
  // source of truth; while the service provider is still loading we default
  // to blocked so users don't briefly see an enabled submit button that the
  // backend would then reject.
  const isServiceProviderOnboarded = useMemo(() => {
    const sp = serviceProvider?.data;
    if (!sp) {
      return false;
    }
    if (sp.payment_service_provider !== 'mangopay') {
      return true;
    }
    return (
      sp.mangopay_kyc_verified === true &&
      sp.mangopay_recipient_status === 'ACTIVE'
    );
  }, [serviceProvider]);

  const submitBatch = useCallback(async () => {
    if (!batchId) {
      return;
    }

    try {
      await submitReceiptBatch({ batchId });
      enqueueSnackbar(
        t('sp-web-app.receipt-batch.receipt-batch-submitted-successfully'),
        {
          variant: 'success',
        }
      );
      navigate({ pathname: spAppMainPages.DASHBOARD });
    } catch (err) {
      let messageTranslationId = 'receipt-batch-submission-failed';
      if (isSubmitReceiptBatchServiceProviderInfoIncompleteException(err)) {
        messageTranslationId = 'receipt-batch-submission-failed-sp-info';
      } else if (
        isSubmitReceiptBatchServiceProviderUserIncompleteException(err)
      ) {
        messageTranslationId = 'receipt-batch-submission-failed-sp-user';
      } else if (isSubmitReceiptBatchNursingHomeInfoIncompleteException(err)) {
        messageTranslationId = 'receipt-batch-submission-failed-nh-info';
      } else if (isSubmitReceiptBatchResidentInfoIncompleteException(err)) {
        messageTranslationId = 'receipt-batch-submission-failed-resident-info';
      } else if (
        isSubmitReceiptBatchServiceProviderNotOnboardedException(err)
      ) {
        messageTranslationId = 'receipt-batch-submission-failed-not-onboarded';
      }
      enqueueErrorSnackbar(
        err,
        t(`sp-web-app.receipt-batch.${messageTranslationId}`),
        {
          variant: 'error',
        }
      );
    }
  }, [
    batchId,
    enqueueSnackbar,
    enqueueErrorSnackbar,
    navigate,
    submitReceiptBatch,
    t,
  ]);

  // @ts-expect-error // getToken is intentionally hidden. It is necessary here to extract the globalServiceProviderId
  const { getToken } = useAuthentication();

  const {
    throttledReceipts,
    isThrottling,
    batch,
    isLoading,
    isError,
    error,
    errorUpdateCount,
    isRefetching,
    totalNumberOfReceipts,
    totalAmount,
    refetchBatch,
    maxReceiptDate,
    minReceiptDate,
  } = useAugmentedThrottledReceipts({
    batchId,
  });

  useEffect(() => {
    if (isError && error instanceof ClientError && error.statusCode === 404) {
      unblockRef.current?.();
      navigate(spAppMainPages.DASHBOARD);
      enqueueErrorSnackbar(error, t('receipts.batch.form.not-found'), {
        variant: 'error',
      });
    }
  }, [t, isError, navigate, errorUpdateCount, error, enqueueErrorSnackbar]);

  const { hasResidentWithReceiptsDisabled, residentsWithReceiptsDisabled } =
    useMemo(() => {
      const archivedResidents = throttledReceipts
        .filter((receipt) => !receipt.residentActive)
        .map((receipt) => {
          // If receipts are not enabled, a resident must be set (since that is based on the resident state)
          return {
            id: receipt.residentId!,
            name: receipt.residentName!,
            gender: receipt.residentGender!,
          };
        });
      return {
        hasResidentWithReceiptsDisabled: archivedResidents.length > 0,
        residentsWithReceiptsDisabled: archivedResidents,
      };
    }, [throttledReceipts]);

  const { mutateAsync: retractReceiptBatch } = useRetractReceiptBatchPhoenix();

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [retractModalState, setRetractModalState] = useState<{
    open: boolean;
    onRetract?: () => void;
  }>({ open: false });
  const [submitModalOpen, setSubmitModalOpen] = useState(false);

  const openRetractModal = (onRetract: () => void) => {
    setRetractModalState({
      open: true,
      onRetract,
    });
  };
  const closeRetractModal = () => {
    setRetractModalState({
      open: false,
    });
  };

  const [internalLoading, setInternalLoading] = useState(false);
  const handleEditClick = useCallback(() => {
    const changeEditMode = () => {
      setInternalLoading(true);
      setMode('edit');
      setTimeout(() => {
        setInternalLoading(false);
      }, 300);
    };

    if (batch?.state === ReceiptBatchState.InReview) {
      openRetractModal(async () => {
        try {
          if (!batchId) {
            throw new Error('batchId is undefined');
          }

          await retractReceiptBatch({ batchId });
          changeEditMode();
          closeRetractModal();
        } catch (err) {
          enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
        }
      });
    } else {
      changeEditMode();
    }
  }, [batch, batchId, enqueueErrorSnackbar, retractReceiptBatch, t]);

  useIdleTimeoutCallback({
    idleTime: 1000 * 30, // 30 seconds
    callback: refetchBatch,
  });

  const { mutateAsync: updateReceiptBatchPhoenix } =
    usePatchReceiptBatchPhoenix();
  const { mutateAsync: deleteReceiptBatch } = useDeleteReceiptBatchPhoenix();
  const { mutateAsync: generateReceipt } = useGenerateReceiptPdf();
  const { mutateAsync: generateReceiptBatch } = useGenerateReceiptBatchPdf();

  const handleSubmit = useCallback(
    async (
      values: ReceiptBatchFormValues,
      helpers: FormikHelpers<ReceiptBatchFormValues>
    ) => {
      if (!batchId) {
        return;
      }
      const token = await getToken();
      const globalServiceProviderId =
        getGlobalServiceProviderIdFromToken(token);
      if (
        (!values.individualDatesMode && !values.receiptDate) ||
        !values.nursingHomeId ||
        !globalServiceProviderId
      ) {
        throw new Error('Missing required fields');
      }
      try {
        const result = await updateReceiptBatchPhoenix({
          ...transformReceiptBatchFormValuesToUpdateReceiptBatchPhoenix(values),
          id: batchId,
        });
        if (result) {
          helpers.resetForm({
            values: transformReceiptBatchPhoenixToReceiptBatchValues(result),
          });
          enqueueSnackbar(t('receipts.batch.form.updated-successfully'), {
            variant: 'success',
          });
          setDirty(false);
          refetchBatch();
          setMode('view');
        }
      } catch (e) {
        enqueueErrorSnackbar(e, t('receipts.batch.form.failed-to-update'), {
          variant: 'error',
        });
      }
    },
    [
      batchId,
      enqueueErrorSnackbar,
      enqueueSnackbar,
      getToken,
      refetchBatch,
      t,
      updateReceiptBatchPhoenix,
    ]
  );

  const handleDeleteBatch = useCallback(async () => {
    if (!batchId) {
      return;
    }
    try {
      unblockRef?.current?.();
      await deleteReceiptBatch({ batchId: batchId });
      enqueueSnackbar(t('receipts.batch.form.delete-batch-success'), {
        variant: 'success',
      });
      navigate(spAppMainPages.DASHBOARD);
    } catch {
      enqueueSnackbar(t('receipts.batch.form.delete-batch-failure'), {
        variant: 'error',
      });
    }
  }, [batchId, deleteReceiptBatch, enqueueSnackbar, navigate, t]);

  const validationSchemaExtension = useMemo(
    () =>
      Yup.object({
        nursingHomeId: Yup.string().required(
          t('service-provider-app.please-selected-a-nursing-home')
        ),
        tenant: Yup.string(),
      }),
    [t]
  );

  const { handleFailedPreviewGenerationError } =
    useHandleFailedPreviewGenerationError();

  const useNursingHomeReceiptForServiceProvider = useFeatureFlagEnabled(
    FeatureFlag.dev_NursingHomeReceiptForServiceProvider
  );
  const { mutateAsync: autoGenerateReceiptsPreviewPhoenixBase } =
    useGenerateReceiptBatchEntryPreview(enqueueErrorSnackbar);

  const autoGenerateReceiptsPreviewPhoenix = useCallback(
    (entryId: string) => {
      return autoGenerateReceiptsPreviewPhoenixBase({
        batchId: batch!.id,
        entryId,
      });
    },
    [autoGenerateReceiptsPreviewPhoenixBase, batch]
  );

  const autoGenerateReceiptsPreview = useCallback(
    async (entryId: string) => {
      if (batch?.id) {
        try {
          const receipt = await generateReceipt({
            batchId: batch.id,
            entryId,
          });
          return receipt;
        } catch (e) {
          handleFailedPreviewGenerationError(e);
        }
      }
      return undefined;
    },
    [batch?.id, generateReceipt, handleFailedPreviewGenerationError]
  );

  const generateReceiptBatchPreview = useCallback(async () => {
    if (batch?.id) {
      try {
        const receipt = await generateReceiptBatch({
          batchId: batch.id,
        });
        return receipt;
      } catch (e) {
        handleFailedPreviewGenerationError(e);
      }
    }
    return undefined;
  }, [batch?.id, generateReceiptBatch, handleFailedPreviewGenerationError]);

  const initialValues: ReceiptBatchFormValues = useMemo(() => {
    return {
      receiptDate: batch?.receiptDate,
      nursingHomeId: batch?.nursingHomeId,
      tenant: batch?.tenant,
      batchImageIds: batch?.receiptImageIds,
      receipts: throttledReceipts,
      generateReceipts: batch?.generateReceipts ?? false,
      individualDatesMode: batch?.useIndividualDates ?? true,
    };
  }, [
    batch?.generateReceipts,
    batch?.nursingHomeId,
    batch?.receiptDate,
    batch?.receiptImageIds,
    batch?.tenant,
    batch?.useIndividualDates,
    throttledReceipts,
  ]);

  const submitButtonDisabled = useMemo(() => {
    return (
      !batch ||
      !isServiceProviderReceiptBatchReadyToSubmit({
        batch,
      }) ||
      isLoading ||
      isRefetching ||
      hasResidentWithReceiptsDisabled ||
      !isServiceProviderOnboarded
    );
  }, [
    batch,
    hasResidentWithReceiptsDisabled,
    isLoading,
    isRefetching,
    isServiceProviderOnboarded,
  ]);

  const shouldNotShowResidentArchivedTooltip = useMemo(() => {
    return (
      batch?.state === ReceiptBatchState.Accepted ||
      batch?.state === ReceiptBatchState.Declined ||
      !residentsWithReceiptsDisabled ||
      residentsWithReceiptsDisabled.length === 0
    );
  }, [batch?.state, residentsWithReceiptsDisabled]);

  const shouldShowReceiptImagesRequiredTooltip = useMemo(() => {
    const anyEntryMissingImage = (batch?.receiptBatchEntries ?? []).some(
      (entry) => {
        return (
          entry.receiptImageIds === undefined ||
          entry.receiptImageIds.length === 0
        );
      }
    );

    const receiptBatchCompleted =
      batch?.state === ReceiptBatchState.Accepted ||
      batch?.state === ReceiptBatchState.Declined;

    return (
      !receiptBatchCompleted &&
      anyEntryMissingImage &&
      !batch?.generateReceipts &&
      batch?.receiptImagesRequired
    );
  }, [batch]);

  const submitButtonTooltip = !isServiceProviderOnboarded
    ? t('receipts.batch.complete-onboarding-to-submit')
    : shouldNotShowResidentArchivedTooltip
    ? shouldShowReceiptImagesRequiredTooltip
      ? t('receipts.batch.receipt-images-required')
      : undefined
    : t('receipts.batch.a-resident-was-archived');

  const numberOfReceipts = throttledReceipts.length;

  if (isLoading || internalLoading) {
    return (
      <Container maxWidth="lg">
        <HeaderBreadcrumbs
          heading={t('dashboard.main')}
          links={[
            {
              name: t('dashboard.main'),
              href: spAppMainPages.DASHBOARD,
            },
            {
              name: t('receipts.batch.edit-receipt-batch-thumbnail'),
            },
          ]}
          sx={{
            pb: 0,
          }}
        />
        <LoadingContainer>
          <CircularProgress variant="indeterminate" />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <ReceiptBatchProvider>
      <Container maxWidth="lg">
        <HeaderBreadcrumbs
          heading={t('dashboard.main')}
          links={[
            {
              name: t('dashboard.main'),
              href: spAppMainPages.DASHBOARD,
            },
            {
              name: t('receipts.batch.edit-receipt-batch-thumbnail'),
            },
          ]}
          sx={{
            pb: 0,
          }}
          action={
            <Stack direction="row" gap={1}>
              {mode === 'view' && (
                <>
                  <RetractReceiptBatchModal
                    open={retractModalState.open}
                    handleClose={closeRetractModal}
                    handleConfirm={retractModalState.onRetract}
                  />
                  <EditOrRetractReceiptBatchButton
                    editable={
                      batch?.state === ReceiptBatchState.Draft ||
                      batch?.state === ReceiptBatchState.ChangeRequested ||
                      batch?.state === ReceiptBatchState.InReview
                    }
                    onEdit={handleEditClick}
                  />
                </>
              )}
              {batchId !== undefined && mode === 'view' && (
                <>
                  <ServiceProviderSubmitReceiptBatchModal
                    open={submitModalOpen}
                    handleClose={() => setSubmitModalOpen(false)}
                    submitDisabled={submitButtonDisabled}
                    handleSubmit={submitBatch}
                    isSubmitting={isSubmitting}
                    numberOfReceipts={numberOfReceipts}
                  />
                  <Tooltip title={submitButtonTooltip} arrow>
                    <div>
                      <Button
                        variant={'contained'}
                        disabled={submitButtonDisabled || isSubmitting}
                        onClick={() => setSubmitModalOpen(true)}
                      >
                        {t('sp-web-app.receipt-batch.submit')}
                      </Button>
                    </div>
                  </Tooltip>
                </>
              )}
              {mode === 'edit' && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (!dirty || window.confirm(t('confirm-page-leave'))) {
                      setMode('view');
                    }
                  }}
                >
                  {t('receipts.batch.form.cancel-edit')}
                </Button>
              )}
            </Stack>
          }
        />

        {!isServiceProviderOnboarded && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => navigate(spAppMainPages.SETUP_BANK_ACCOUNT)}
              >
                {t('receipts.batch.complete-onboarding-action')}
              </Button>
            }
          >
            {t('receipts.batch.complete-onboarding-to-submit')}
          </Alert>
        )}

        {mode === 'edit' ? (
          <ServiceProviderReceiptBatchForm
            initialValues={initialValues}
            archivedResidents={residentsWithReceiptsDisabled}
            validationSchemaExtension={validationSchemaExtension}
            handleSubmit={handleSubmit}
            handleDeleteBatch={
              batch && batch.state === ReceiptBatchState.Draft
                ? handleDeleteBatch
                : undefined
            }
            isLoading={isThrottling || isLoading}
            blockSubmit={isRefetching || isThrottling || isLoading}
            onChangeDirty={setDirty}
            allowNavigateAway={false}
            useRenderReceiptBatchRow={useServiceProviderRenderReceiptBatchRow}
            usePreviewImageIds={usePreviewImageIds}
            renderReceiptBatchImageInput={renderReceiptBatchImageInput}
            renderReceiptBatchCommonForm={renderReceiptBatchCommonFormEdit}
            unblockRef={unblockRef}
          />
        ) : (
          <ReceiptBatchDetailsContainer
            id={batchId}
            isLoading={isThrottling || isLoading}
            batchImageIds={batch?.receiptImageIds}
            receipts={throttledReceipts.map((receipt) => ({
              ...receipt,
              individualDate: batch?.useIndividualDates
                ? receipt.individualDate
                : undefined,
            }))}
            autoGenerateReceipts={batch?.generateReceipts}
            autoGenerateReceiptsPreview={
              useNursingHomeReceiptForServiceProvider
                ? autoGenerateReceiptsPreviewPhoenix
                : autoGenerateReceiptsPreview
            }
            generateReceiptBatchPreview={generateReceiptBatchPreview}
            receiptDate={batch?.receiptDate}
            useIndividualDates={batch?.useIndividualDates ?? false}
            minReceiptDate={minReceiptDate}
            maxReceiptDate={maxReceiptDate}
            totalAmount={totalAmount}
            receiptsCount={totalNumberOfReceipts}
            nursingHomeName={batch?.nursingHomeName}
            tenant={batch?.tenant}
            state={batch?.state}
            lastUpdateDate={batch?.updatedOn}
          />
        )}
      </Container>
    </ReceiptBatchProvider>
  );
};

export default EditReceiptBatchPage;
